import React, { Fragment, ReactElement, useContext, useEffect, useMemo, useState } from 'react'
import TableRow from '@material-ui/core/TableRow'
import { Table, TableCell, TableHead } from '@material-ui/core'
import TableBody from '@material-ui/core/TableBody'
import { Provisioning as ProvisioningLanguage } from '../../../../i18n'
import CLanguageHandler from '../../../NonReact/Languages/LanguageHandler'
import { TelSysFields } from '../../../NonReact/constants/ApiConstants'
import { ArrayField, UseFormMethods } from '../../../Vendor/react-hook-form/dist/index.ie11'
import { ReportingAccessLevels } from '../../AddUser/Reporting/ReportingAccessLevels'
import { ALL_TEL_OPTION, ALL_VALUE, getTelServersAsSelectOptionsWithCMEScopeCheck } from '../../Common/API/ItemsGetters'
import ContextMenu, { ContextMenuPosition } from '../../Common/Components/ContextMenu'
import Icon, { IconName } from '../../Common/Components/Icon'
import IconButton, { IconButtonType } from '../../Common/Components/IconButton'
import { SelectOptions } from '../../Common/Components/Selects/Select/Select'
import { EntitySelectOption, SelectOption } from '../../Common/Components/Selects/Types'
import { getFieldError, ValidationErrors } from '../../Common/Components/ValidationErrors/ValidationErrors'
import { ANY_VALUE_NUMBER_NUMERIC } from '../../Common/Constants/DefaultValues'
import { DISPLAY_NONE_VALUE } from '../../Common/Constants/Styling'
import { LanguageContext, ReactState, UserInfoContext } from '../../Common/Contexts'
import { UserInfoContextType } from '../../Common/Contexts/UserInfoContext'
import { useFeatureFlag } from '../../Common/Hooks/useFeatureFlag'
import { TelSys } from '../../Common/Types/Types'
import { displayError } from '../../Common/Utils/Messages'
import {
    PROVISIONING_ITEM_TYPE_1000,
    PROVISIONING_ITEM_TYPE_2000,
    PROVISIONING_ITEM_TYPE_ACD_AGENT,
    PROVISIONING_ITEM_TYPE_CALL_CENTRE_AGENT,
    PROVISIONING_ITEM_TYPE_CALL_CENTRE_SUPERVISOR,
    PROVISIONING_ITEM_TYPE_ENTERPRISE,
    PROVISIONING_ITEM_TYPE_ESSENTIALS,
    PROVISIONING_ITEM_TYPE_EXTENSION,
    PROVISIONING_ITEM_TYPE_HOSPITALITY,
    PROVISIONING_ITEM_TYPE_PROFESSIONAL
} from '../Constants'
import {
    CMEProvisioningBroadworksServicePackEntry,
    CMEProvisioningBroadworksServicePackEntryField,
    enterpriseFieldName,
    legacyFieldName,
    ProvisioningBroadworksServicePackEntryFieldNames,
    servicepacksNameSpacing
} from '../FieldName'
import { CMEFormHookData, ServicePackItem, ServicePackItemForm } from '../Types'
import { OutlinedSelect } from '../../../ReactRouting/Components/Atoms/Inputs/OutlinedSelect'
import { RouterCheckbox } from '../../../ReactRouting/Components/Atoms/Inputs/RouterCheckbox'
import { OutlinedTextInput } from '../../../ReactRouting/Components/Atoms/Inputs/OutlinedTextInput'
import { OutlinedSelectMulti } from '../../../ReactRouting/Components/Atoms/Inputs/OutlinedSelectMulti'
import FormRow from '../../Common/Components/FormRow'
import { EMPTY_STRING } from '../../../NonReact/constants/Common'

type Props = {
    cmeID: string
    servicePackValues: Partial<ArrayField<ServicePackItemForm, 'id'>>[]
    addNewServicePackItem: (servicePackItem: ServicePackItemForm) => void
    removeServicePackItem: (index: number) => void
    methods: UseFormMethods<CMEFormHookData>
    isViewOnly?: boolean
}

type ProvisioningTypeSelectOption = SelectOption<string> & {
    enterpriseOnly?: boolean
    legacyOnly?: boolean
}

/**
 * Configuration tabs container component.
 * @param cmeID ID of CME.
 * @param servicePackValues Array of values from formState.
 * @param addNewServicePackItem Method that appends new servicePackItem to servicePackValues.
 * @param removeServicePackItem Method that removes servicePackItem based on index in the list.
 * @param methods ../../../Vendor/../../../../Vendor/../../../Vendor/react-hook-form/dist/index.ie11'
 * @param isViewOnly View only flag.
 */
const Provisioning: React.FC<Props> = ({
    cmeID,
    servicePackValues,
    addNewServicePackItem,
    removeServicePackItem,
    methods: { setValue, getValues, watch, trigger, clearErrors, errors },
    isViewOnly
}: Props): ReactElement => {
    const language: CLanguageHandler = useContext(LanguageContext) as CLanguageHandler
    const userInfo: UserInfoContextType = useContext(UserInfoContext)

    language.loadBundle(ProvisioningLanguage.NAME)

    const showUserTypeServicePacks: Definable<boolean> = useFeatureFlag('showUserTypeServicePacks')

    const itemTypeOptions: ProvisioningTypeSelectOption[] = [
        {
            label: language.get(ProvisioningLanguage.BroadworksProvisioningItemExtension),
            value: PROVISIONING_ITEM_TYPE_EXTENSION
        },
        {
            label: language.get(ProvisioningLanguage.BroadworksProvisioningItemHospitality),
            value: PROVISIONING_ITEM_TYPE_HOSPITALITY
        },
        {
            label: language.get(ProvisioningLanguage.BroadworksProvisioningItemAcdAgent),
            value: PROVISIONING_ITEM_TYPE_ACD_AGENT
        },
        ...(showUserTypeServicePacks
            ? [
                  {
                      label: language.get(ProvisioningLanguage.BroadworksProvisioningItemEssentials),
                      value: PROVISIONING_ITEM_TYPE_ESSENTIALS,
                      enterpriseOnly: true
                  },
                  {
                      label: language.get(ProvisioningLanguage.BroadworksProvisioningItemProfessional),
                      value: PROVISIONING_ITEM_TYPE_PROFESSIONAL,
                      enterpriseOnly: true
                  },
                  {
                      label: language.get(ProvisioningLanguage.BroadworksProvisioningItemEnterprise),
                      value: PROVISIONING_ITEM_TYPE_ENTERPRISE,
                      enterpriseOnly: true
                  },
                  {
                      label: language.get(ProvisioningLanguage.BroadworksProvisioningItemCallCentreAgent),
                      value: PROVISIONING_ITEM_TYPE_CALL_CENTRE_AGENT,
                      enterpriseOnly: true
                  },
                  {
                      label: language.get(ProvisioningLanguage.BroadworksProvisioningItemCallCentreSupervisor),
                      value: PROVISIONING_ITEM_TYPE_CALL_CENTRE_SUPERVISOR,
                      enterpriseOnly: true
                  },
                  {
                      label: language.get(ProvisioningLanguage.BroadworksProvisioningItem1000),
                      value: PROVISIONING_ITEM_TYPE_1000,
                      legacyOnly: true
                  },
                  {
                      label: language.get(ProvisioningLanguage.BroadworksProvisioningItem2000),
                      value: PROVISIONING_ITEM_TYPE_2000,
                      legacyOnly: true
                  }
              ]
            : [])
    ]

    const [availableTelServers, setAvailableTelServers]: ReactState<EntitySelectOption<number, TelSys>[]> = useState<
        EntitySelectOption<number, TelSys>[]
    >([ALL_TEL_OPTION])
    const availableEnterpriseTelServers: EntitySelectOption<number, TelSys>[] = useMemo(
        (): EntitySelectOption<number, TelSys>[] => [
            ALL_TEL_OPTION,
            ...availableTelServers.filter(
                (availableTelServer: EntitySelectOption<number, TelSys>): boolean =>
                    !!(
                        availableTelServer.original?.PartitionLicences?.length === 0 ||
                        availableTelServer.original?.PartitionLicences?.every(
                            (partitionLicence: string): boolean => partitionLicence === ReportingAccessLevels.ENTERPRISE
                        )
                    )
            )
        ],
        [JSON.stringify(availableTelServers)]
    )
    const availableLegacyTelServers: EntitySelectOption<number, TelSys>[] = useMemo(
        (): EntitySelectOption<number, TelSys>[] => [
            ALL_TEL_OPTION,
            ...availableTelServers.filter(
                (availableTelServer: EntitySelectOption<number, TelSys>): boolean =>
                    !!(
                        availableTelServer.original?.PartitionLicences?.length === 0 ||
                        availableTelServer.original?.PartitionLicences?.some((partitionLicence: string): boolean =>
                            [
                                ReportingAccessLevels.BLF,
                                ReportingAccessLevels.LITE,
                                ReportingAccessLevels.ONE,
                                ReportingAccessLevels.TWO_THOUSAND,
                                ReportingAccessLevels.ONE_THOUSAND,
                                ReportingAccessLevels.NONE
                            ].includes(partitionLicence as unknown as ReportingAccessLevels)
                        )
                    )
            )
        ],
        [JSON.stringify(availableTelServers)]
    )

    const [hoveredLine, setHoveredLine] = useState<number>(ANY_VALUE_NUMBER_NUMERIC)
    const [editingIndex, setEditingIndex] = useState<number>(ANY_VALUE_NUMBER_NUMERIC)
    const [valueBeforeEdit, setValueBeforeEdit] =
        useState<Nullable<Partial<ArrayField<ServicePackItemForm, 'id'>>>>(null)

    useEffect((): void => {
        if (cmeID) {
            getTelServersAsSelectOptionsWithCMEScopeCheck(cmeID, false, userInfo, [TelSysFields.PARTITION_LICENCES])
                .then(setAvailableTelServers)
                .catch(displayError)
        }
    }, [userInfo, cmeID])

    const handleTelSysChange = (values: number[], index: number): void => {
        const previousValues: number[] = getValues<string, number[]>(
            CMEProvisioningBroadworksServicePackEntryField(
                ProvisioningBroadworksServicePackEntryFieldNames.TELSYS_IDS,
                index
            )
        )
        let newValues = values

        // If All Telephony Servers value was selected, it should be the only value.
        if (!previousValues.includes(ALL_VALUE) && newValues.includes(ALL_VALUE)) {
            newValues = [ALL_VALUE]
        } else if (newValues?.length === 0) {
            newValues = [ALL_VALUE]
        } else if (newValues.includes(ALL_VALUE) && newValues.length > 1) {
            newValues = newValues.filter((value) => value !== ALL_VALUE)
        }
        setValue(
            CMEProvisioningBroadworksServicePackEntryField(
                ProvisioningBroadworksServicePackEntryFieldNames.TELSYS_IDS,
                index
            ),
            newValues
        )
    }

    const discardCurrentlyEditedRowChanges = (index: number): void => {
        if (!valueBeforeEdit) {
            removeServicePackItem(index)
        } else if (valueBeforeEdit) {
            // Roll back value.
            setValue(CMEProvisioningBroadworksServicePackEntry(index), valueBeforeEdit)
            clearErrors(CMEProvisioningBroadworksServicePackEntry(index))
        }
        setEditingIndex(ANY_VALUE_NUMBER_NUMERIC)
    }

    const persistCurrentlyEditedRow = (): Promise<boolean> => {
        if (editingIndex > ANY_VALUE_NUMBER_NUMERIC) {
            return trigger([
                CMEProvisioningBroadworksServicePackEntryField(
                    ProvisioningBroadworksServicePackEntryFieldNames.VALUE,
                    editingIndex
                )
            ]).then((validationResult) => {
                if (validationResult) {
                    setEditingIndex(ANY_VALUE_NUMBER_NUMERIC)
                    setValueBeforeEdit(null)
                    return true
                }
                return false
            })
        }
        // Return true to indicate that action was successful (even though no row was persisted).
        return Promise.resolve(true)
    }

    const editRow = (index: number, item: Nullable<Partial<ServicePackItemForm>>): void => {
        setEditingIndex(index)
        setValueBeforeEdit(item)
    }

    const addNewServicePackRow = (): void => {
        // If there is row being edited, save the changes.
        persistCurrentlyEditedRow().then((persistResult) => {
            if (persistResult) {
                // Only create new row if the previous one was successfully persisted.
                const newItem: ServicePackItemForm = {
                    Value: '',
                    TelSysIDs: [ALL_VALUE],
                    ItemType: itemTypeOptions[0].value,
                    Enabled: false,
                    SendWelcomeEmail: false
                }

                addNewServicePackItem(newItem)
                editRow(servicePackValues.length, null)
            }
        })
    }

    const servicePackName = (index: number): string =>
        CMEProvisioningBroadworksServicePackEntryField(ProvisioningBroadworksServicePackEntryFieldNames.VALUE, index)

    const allEntries: ServicePackItem[] = watch(servicepacksNameSpacing)

    const isSendWelcomeAvailable: boolean = useMemo(
        (): boolean =>
            allEntries?.some((entry: ServicePackItem): boolean => {
                const selectedItemType: Nullable<ProvisioningTypeSelectOption> = itemTypeOptions.find(
                    (option: ProvisioningTypeSelectOption): boolean => option.value === entry.ItemType
                )

                return !!(selectedItemType?.enterpriseOnly || selectedItemType?.legacyOnly)
            }),
        [allEntries]
    )

    const table: ReactElement = (
        <Table className='broadworks-table'>
            <TableHead>
                <TableRow>
                    <TableCell className='broadworks-table-header item-header'>
                        {language.get(ProvisioningLanguage.BroadworksProvisioningTableHeaderItem)}
                    </TableCell>
                    <TableCell className='broadworks-table-header center-align'>
                        {language.get(ProvisioningLanguage.BroadworksProvisioningTableHeaderEnabled)}
                    </TableCell>
                    <TableCell className='broadworks-table-header service-pack-header'>
                        {language.get(ProvisioningLanguage.BroadworksProvisioningTableHeaderServicePack)}
                    </TableCell>
                    <TableCell className='broadworks-table-header'>
                        {language.get(ProvisioningLanguage.BroadworksProvisioningTableHeaderTelephonyServers)}
                    </TableCell>
                    {isSendWelcomeAvailable ? (
                        <TableCell className='broadworks-table-header' colSpan={2}>
                            {language.get(ProvisioningLanguage.BroadworksProvisioningTableHeaderSendWelcomeEmail)}
                        </TableCell>
                    ) : (
                        <TableCell className='broadworks-table-header actions-header' />
                    )}
                </TableRow>
            </TableHead>
            <TableBody>
                {servicePackValues.map((item, index) => {
                    const isEditingRow: boolean = editingIndex === index
                    const isHoveredRow: boolean = hoveredLine === index

                    const itemTypeFieldName: string = CMEProvisioningBroadworksServicePackEntryField(
                        ProvisioningBroadworksServicePackEntryFieldNames.ITEM_TYPE,
                        index
                    )
                    const itemTelSysIdsFieldName: string = CMEProvisioningBroadworksServicePackEntryField(
                        ProvisioningBroadworksServicePackEntryFieldNames.TELSYS_IDS,
                        index
                    )
                    const itemValueFieldName: string = CMEProvisioningBroadworksServicePackEntryField(
                        ProvisioningBroadworksServicePackEntryFieldNames.VALUE,
                        index
                    )
                    const itemSendWelcomeEmailFieldName: string = CMEProvisioningBroadworksServicePackEntryField(
                        ProvisioningBroadworksServicePackEntryFieldNames.SEND_WELCOME_EMAIL,
                        index
                    )

                    const selectedTelSysIDs: number[] = watch(itemTelSysIdsFieldName)

                    let selectedTelSyss: string = EMPTY_STRING

                    if (selectedTelSysIDs) {
                        // Translate TelSysIDs into descriptions.
                        selectedTelSyss = availableTelServers
                            .filter((option) => selectedTelSysIDs.includes(option.value))
                            .map((option) => option.label)
                            .join(', ')
                    }

                    const selectedItemTypeValue: Nullable<string> = watch(itemTypeFieldName)
                    const selectedItemType: Nullable<ProvisioningTypeSelectOption> = itemTypeOptions.find(
                        (option: ProvisioningTypeSelectOption) => option.value === selectedItemTypeValue
                    )

                    const isLegacyCapabilityEnabled: boolean = watch(legacyFieldName)
                    const isEnterpriseCapabilityEnabled: boolean = watch(enterpriseFieldName)

                    const itemTypes: ProvisioningTypeSelectOption[] = itemTypeOptions.filter(
                        (itemTypeOption: ProvisioningTypeSelectOption): boolean => {
                            if (!isEnterpriseCapabilityEnabled && itemTypeOption.enterpriseOnly) {
                                return false
                            }

                            if (!isLegacyCapabilityEnabled && itemTypeOption.legacyOnly) {
                                return false
                            }

                            return true
                        }
                    )

                    const isEnterpriseOnly: boolean = !!selectedItemType?.enterpriseOnly
                    const isLegacyOnly: boolean = !!selectedItemType?.legacyOnly
                    const isUserSync: boolean = isEnterpriseOnly || isLegacyOnly
                    const selectedItemTypeLabel: Nullable<string> = selectedItemType?.label

                    let availableTelSyss: SelectOptions<number> = availableTelServers

                    if (isEnterpriseOnly) {
                        availableTelSyss = availableEnterpriseTelServers
                    }

                    if (isLegacyOnly) {
                        availableTelSyss = availableLegacyTelServers
                    }

                    return (
                        <Fragment key={`${item.id}-RowContainer`}>
                            <TableRow
                                key={`${item.id}-BroadworksTableRow`}
                                className={`broadworks-table-row ${isHoveredRow ? 'highlight-row' : ''}`}
                                onMouseEnter={(): void => {
                                    setHoveredLine(index)
                                }}
                                onMouseLeave={(): void => {
                                    setHoveredLine(ANY_VALUE_NUMBER_NUMERIC)
                                }}
                            >
                                <>
                                    <TableCell
                                        className='broadworks-table-cell'
                                        style={{
                                            paddingLeft: !isEditingRow ? '1rem' : undefined
                                        }}
                                    >
                                        <div style={!isEditingRow ? { display: DISPLAY_NONE_VALUE } : {}}>
                                            <FormRow>
                                                <OutlinedSelect
                                                    key={`${item.id}-ItemType`}
                                                    options={itemTypes}
                                                    name={itemTypeFieldName}
                                                    renderSelectedValue={false}
                                                    handleChange={(value: string): void => {
                                                        setValue(itemTypeFieldName, value)
                                                    }}
                                                    disabled={isViewOnly}
                                                />
                                            </FormRow>
                                        </div>
                                        {!isEditingRow && <div>{selectedItemTypeLabel}</div>}
                                    </TableCell>
                                    <TableCell className='broadworks-table-cell'>
                                        <div className='center-field'>
                                            <RouterCheckbox
                                                key={`${item.id}-Enabled`}
                                                name={CMEProvisioningBroadworksServicePackEntryField(
                                                    ProvisioningBroadworksServicePackEntryFieldNames.ENABLED,
                                                    index
                                                )}
                                                defaultChecked={false}
                                                disabled={isViewOnly || isEditingRow}
                                            />
                                        </div>
                                    </TableCell>
                                    <TableCell className='broadworks-table-cell'>
                                        <div>
                                            <div style={!isEditingRow ? { display: DISPLAY_NONE_VALUE } : {}}>
                                                <FormRow>
                                                    <OutlinedTextInput
                                                        key={`${item.id}-Value`}
                                                        inputName={itemValueFieldName}
                                                        rules={{
                                                            required: {
                                                                value: true,
                                                                message: language?.get(
                                                                    ProvisioningLanguage.BroadworksProvisioningValueIsRequired
                                                                )
                                                            }
                                                        }}
                                                        // eslint-disable-next-line @typescript-eslint/no-misused-promises
                                                        onChange={(): Promise<boolean> => trigger(itemValueFieldName)}
                                                        disabled={isViewOnly}
                                                    />
                                                </FormRow>
                                            </div>
                                            {!isEditingRow && <div>{watch(itemValueFieldName)}</div>}
                                        </div>
                                    </TableCell>
                                    <TableCell className='broadworks-table-cell'>
                                        <div style={!isEditingRow ? { display: DISPLAY_NONE_VALUE } : {}}>
                                            {/* Check for selectedTelSysIDs forces re-render of select. which solves issues with value not rendering correctly when adding new rows */}
                                            {selectedTelSysIDs && (
                                                <FormRow>
                                                    <OutlinedSelectMulti
                                                        key={`${item.id}-TelSysIDs`}
                                                        options={availableTelSyss}
                                                        name={itemTelSysIdsFieldName}
                                                        handleChange={(values: number[]): void =>
                                                            handleTelSysChange(values, index)
                                                        }
                                                    />
                                                </FormRow>
                                            )}
                                        </div>
                                        {!isEditingRow && <div>{selectedTelSyss}</div>}
                                    </TableCell>
                                    {isSendWelcomeAvailable ? (
                                        <TableCell className='broadworks-table-cell'>
                                            {isUserSync ? (
                                                <div className='center-field'>
                                                    <RouterCheckbox
                                                        key={`${item.id}-SendWelcomeEmail`}
                                                        name={itemSendWelcomeEmailFieldName}
                                                        defaultChecked={false}
                                                        disabled={isViewOnly}
                                                    />
                                                </div>
                                            ) : (
                                                <></>
                                            )}
                                        </TableCell>
                                    ) : (
                                        <></>
                                    )}
                                </>

                                <TableCell className='broadworks-table-cell'>
                                    {!isViewOnly ? (
                                        <>
                                            {!isEditingRow ? (
                                                <ContextMenu
                                                    menuItems={[
                                                        {
                                                            itemName: language?.get(
                                                                ProvisioningLanguage.BroadworksProvisioningButtonDelete
                                                            ),
                                                            isEnabled: true,
                                                            icon: <Icon iconName={IconName.DELETE} />,
                                                            onClick: (): void => {
                                                                removeServicePackItem(index)
                                                            }
                                                        },
                                                        {
                                                            itemName: language?.get(
                                                                ProvisioningLanguage.BroadworksProvisioningButtonEdit
                                                            ),
                                                            isEnabled: true,
                                                            icon: <Icon iconName={IconName.PENCIL} />,
                                                            onClick: (): void => {
                                                                setEditingIndex(index)
                                                                setValueBeforeEdit(item)
                                                            }
                                                        }
                                                    ]}
                                                    position={ContextMenuPosition.TOP_RIGHT}
                                                    anchorElement={
                                                        <div className='edit-icon-wrapper'>
                                                            <IconButton
                                                                type={IconButtonType.BUTTON}
                                                                iconName={IconName.MORE_HORIZONTAL}
                                                                title={language?.get(
                                                                    ProvisioningLanguage.BroadworksProvisioningButtonActions
                                                                )}
                                                            />
                                                        </div>
                                                    }
                                                />
                                            ) : (
                                                <div className={'actions'}>
                                                    <div className='edit-icon-wrapper'>
                                                        <IconButton
                                                            type={IconButtonType.BUTTON}
                                                            iconName={IconName.CHECK}
                                                            title={language?.get(
                                                                ProvisioningLanguage.BroadworksProvisioningButtonSave
                                                            )}
                                                            // eslint-disable-next-line @typescript-eslint/no-misused-promises
                                                            onClick={async (e): Promise<void> => {
                                                                const isValid = trigger(
                                                                    CMEProvisioningBroadworksServicePackEntryField(
                                                                        ProvisioningBroadworksServicePackEntryFieldNames.VALUE,
                                                                        index
                                                                    )
                                                                )
                                                                if (await isValid) {
                                                                    e?.preventDefault()
                                                                    e?.stopPropagation()
                                                                    setEditingIndex(ANY_VALUE_NUMBER_NUMERIC)
                                                                    setValueBeforeEdit(null)
                                                                }
                                                            }}
                                                        />
                                                    </div>
                                                    <div className='edit-icon-wrapper'>
                                                        <IconButton
                                                            type={IconButtonType.BUTTON}
                                                            iconName={IconName.X}
                                                            title={language?.get(
                                                                ProvisioningLanguage.BroadworksProvisioningButtonDiscardChanges
                                                            )}
                                                            onClick={(): void =>
                                                                discardCurrentlyEditedRowChanges(index)
                                                            }
                                                        />
                                                    </div>
                                                </div>
                                            )}
                                        </>
                                    ) : (
                                        <></>
                                    )}
                                </TableCell>
                            </TableRow>
                            {getFieldError(servicePackName(index), errors) ? (
                                <tr className='broadworks-provisioning-error-row' key={`${item.id}-ErrorRow`}>
                                    <td colSpan={1} className='broadworks-table-cell'>
                                        &nbsp;
                                    </td>
                                    <td colSpan={1} className='broadworks-table-cell'>
                                        &nbsp;
                                    </td>
                                    <td colSpan={1} className='broadworks-table-cell'>
                                        <ValidationErrors name={servicePackName(index)} />
                                    </td>
                                    <td colSpan={1} className='broadworks-table-cell'>
                                        &nbsp;
                                    </td>
                                    <td colSpan={1} className='broadworks-table-cell'>
                                        &nbsp;
                                    </td>
                                </tr>
                            ) : (
                                <></>
                            )}
                        </Fragment>
                    )
                })}
                <TableRow>
                    <TableCell colSpan={isSendWelcomeAvailable ? 6 : 5}>
                        {!isViewOnly ? (
                            <div className='clickable-text' onClick={addNewServicePackRow}>
                                {language.get(ProvisioningLanguage.BroadworksProvisioningAddEntry)}
                            </div>
                        ) : (
                            <></>
                        )}
                    </TableCell>
                </TableRow>
            </TableBody>
        </Table>
    )

    return (
        <div className='provisioning-tab tab-area'>
            <div className='details-table'>
                <h3 className='dt-main-header'>{language.get(ProvisioningLanguage.BroadworksProvisioningHeader)}</h3>
                <div className='broadworks-content'>{table}</div>
            </div>
        </div>
    )
}

export default Provisioning
